import { useEffect, useState } from "react";
import { api, API, formatINR, formatDate, methodLabel, packageLabel } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { Download, AlertCircle, Loader2, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

function OutstandingList({ serviceType }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { service_type: serviceType, status: "pending" } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serviceType]);

  // Client-side filter (list is small enough — outstanding customers only)
  const filtered = q.trim()
    ? rows.filter((c) => {
        const t = q.trim().toLowerCase();
        return (
          (c.name || "").toLowerCase().includes(t) ||
          (c.phone || "").toLowerCase().includes(t) ||
          (c.customer_code || "").toLowerCase().includes(t)
        );
      })
    : rows;

  const totalOutstanding = filtered.reduce((s, r) => s + (r.balance_due || 0), 0);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/customers/outstanding/export?service_type=${serviceType}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `outstanding_${serviceType}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-red-50 text-red-600 grid place-items-center">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">{filtered.length} customer{filtered.length === 1 ? "" : "s"} pending</div>
            <div className="pds-stat-num text-2xl text-red-700">{formatINR(totalOutstanding)}</div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              data-testid={`outstanding-search-${serviceType}`}
              className="pl-9"
              placeholder={`Search ${serviceType === "internet" ? "user id" : "membership"}, name or phone…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <Button
            data-testid={`export-outstanding-${serviceType}`}
            variant="outline"
            onClick={downloadExcel}
            disabled={downloading || rows.length === 0}
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Export Excel
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid={`outstanding-table-${serviceType}`}>
          <TableHeader>
            <TableRow>
              <TableHead>{serviceType === "internet" ? "User ID" : "Membership"}</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Last Payment</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">{q ? "No matches." : "No outstanding customers."} </TableCell></TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} className="hover:bg-gray-50">
                <TableCell className="font-medium">{c.customer_code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.phone}</TableCell>
                <TableCell>{packageLabel(c.service_type, c.package)}</TableCell>
                <TableCell>
                  {c.last_payment_method ? (
                    <div className="text-xs">
                      <div className="font-medium text-gray-800">{methodLabel(c.last_payment_method)}</div>
                      <div className="text-gray-500">{formatDate(c.last_payment_at)}</div>
                    </div>
                  ) : <span className="text-gray-400 text-sm">Never</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <span className="status-chip status-pending">{formatINR(c.balance_due)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export default function OutstandingPage() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Outstanding</h1>
        <p className="text-gray-500 mt-1 text-sm">Customers with pending balance. Download the list as Excel anytime.</p>
      </div>
      <Tabs defaultValue="internet">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="internet" data-testid="outstanding-tab-internet">Internet</TabsTrigger>
          <TabsTrigger value="cable" data-testid="outstanding-tab-cable">Cable TV</TabsTrigger>
        </TabsList>
        <TabsContent value="internet" className="mt-4">
          <OutstandingList serviceType="internet" />
        </TabsContent>
        <TabsContent value="cable" className="mt-4">
          <OutstandingList serviceType="cable" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
